import { DataListItem } from '@brighte/form-components';
import { Period } from '@brighte/validation';
import { useApplicationData } from '@provider';
import { HOUSEHOLD, INCOME_VERIFICATION, LABELS, formatDollars } from '@utils';

function useHouseholdSummary({
  isCashApplication,
}: {
  isCashApplication: boolean;
}): Array<{ title: string; items: DataListItem[] | [] }> | [] {
  const { HouseholdDetails: householdDetails } = useApplicationData(['HouseholdDetails']);

  if (!householdDetails) return [];

  const { BANK_STATEMENT, DOCUMENTS, VERIFICATION } = INCOME_VERIFICATION;

  if (isCashApplication) {
    const { income } = householdDetails.applicant;
    const hasDocuments = !!income?.primary?.bankStatementToken || income?.primary?.documents?.length > 0;

    return income?.householdIncome
      ? [
          {
            title: '',
            items: [
              {
                label: LABELS.HOUSEHOLD_INCOME,
                value: `${formatDollars(income.householdIncome.amount)} ${(income.householdIncome.period || Period.Monthly).toLowerCase()}`,
              },
              ...(hasDocuments
                ? [
                    {
                      label: VERIFICATION,
                      value: income?.primary?.bankStatementToken ? BANK_STATEMENT : DOCUMENTS,
                    },
                  ]
                : []),
            ],
          },
        ]
      : [];
  }

  const { maritalStatus, dependents, expense } = householdDetails.applicant;

  const { MORTGAGE_REPAYMENTS, RENT, OTHER_LOANS, CREDIT_CARDS, HOUSE_LIVING_EXPENSES, INVESTMENT_PROPERTIES } = LABELS;

  if (expense) {
    const { mortgage, rent, otherLoans, creditCards, household, investmentProperties } = expense;

    // `emptyText` overrides the default "No <label>" summary value when a field is not applicable —
    // used where the long "No <label>" phrasing reads as a confusing statement rather than a value.
    const expenses = [
      { expense: mortgage, label: MORTGAGE_REPAYMENTS, emptyText: 'None' },
      { expense: rent, label: RENT },
      { expense: investmentProperties, label: INVESTMENT_PROPERTIES, emptyText: 'None' },
      { expense: otherLoans, label: OTHER_LOANS },
      { expense: creditCards, label: CREDIT_CARDS },
      { expense: household, label: HOUSE_LIVING_EXPENSES },
    ];

    const items = expenses
      .filter(({ expense }) => !!expense)
      .map(({ expense, label, emptyText }) => {
        const value = expense.isNotApplicable
          ? (emptyText ?? `No ${label.toLowerCase()}`)
          : `${formatDollars(expense.amount || expense.limit)} ${(expense.period || Period.Monthly).toLowerCase()}`;

        return { label, value };
      });

    const maritalStatusLabel = HOUSEHOLD.MARITAL_STATUS_OPTIONS.find(opt => opt.value === maritalStatus)?.label ?? '';

    const householdSummary = [
      {
        title: dependents ? `${maritalStatusLabel}, ${dependents} dependents` : maritalStatusLabel,
        items,
      },
    ];

    return householdSummary;
  }
  return [];
}

export { useHouseholdSummary };
